'use client';

import FaIcon, { faXmark } from '@/components/icon/FaIcon/FaIcon';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import type { ItemFilterProduct } from '@/features/gfWorks/types';
import { memo, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './ItemFilterModal.module.scss';

// フィルタを開くボタンの文言（ItemFilterTrigger）と揃える。SP / PC 共通。
const MODAL_TITLE = 'つくりたいアイテムで絞り込む';
// 開閉アニメーションの長さ（scss の transition と揃える）
export const TRANSITION_MS = 200;

/**
 * 選択中アイコンを着色する SVG フィルタの id。scss の `filter: url(...)` と揃える。
 *
 * アイコンは線画PNGで fill が使えないため、以前は brightness/hue-rotate 等を7段重ねた
 * CSSフィルタで赤を作っていた。あれは色を指定しているのではなく「計算結果がたまたま赤になる値」で、
 * 色空間の解釈がブラウザで異なるため iOS Safari だけ橙色に転んでいた（GF-2378 の再発）。
 * feFlood + feComposite なら元画像の色を一切参照せず、アルファの形に指定色を流し込むだけなので
 * 計算が挟まらず、どのブラウザでも --color-red そのものになる。
 */
const SELECTED_TINT_FILTER_ID = 'item-filter-selected-tint';

// 非活性 / 選択中 / 非選択で控えめ は同時に成立しないため、行の状態クラスは常に1つだけ付ける。
// 同一要素に複数付けると、同じプロパティを持つクラスの適用順で見た目が変わってしまう
const resolveRowStateClass = ({
  isUnavailable,
  isSelected,
  isDimmed,
}: { isUnavailable: boolean; isSelected: boolean; isDimmed: boolean }) => {
  if (isUnavailable) return styles.productRowUnavailable;
  if (isSelected) return styles.productRowSelected;
  if (isDimmed) return styles.productRowDimmed;
  return '';
};

function ItemFilterModal({
  isOpen,
  onClose,
  products,
  selectedProductIds,
  onToggleProduct,
  onClear,
}: {
  isOpen: boolean;
  onClose: () => void;
  products: ItemFilterProduct[];
  selectedProductIds: number[];
  onToggleProduct: (productId: number) => void;
  onClear: () => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  // スクロールロックの元の値と、閉じ際の遅延復元タイマー
  const originalOverflowRef = useRef<string | null>(null);
  const restoreScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // 連続開閉で復元が予約済みならキャンセルし、ロックを維持する
    if (restoreScrollTimerRef.current) {
      clearTimeout(restoreScrollTimerRef.current);
      restoreScrollTimerRef.current = null;
    }
    if (originalOverflowRef.current === null) {
      originalOverflowRef.current = document.body.style.overflow;
    }
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // exit アニメーション(TRANSITION_MS)の間は背面をロックしたままにし、閉じ切ってから復元する
      restoreScrollTimerRef.current = setTimeout(() => {
        if (originalOverflowRef.current !== null) {
          document.body.style.overflow = originalOverflowRef.current;
          originalOverflowRef.current = null;
        }
        restoreScrollTimerRef.current = null;
      }, TRANSITION_MS);
    };
  }, [isOpen, onClose]);

  const modalTitle = MODAL_TITLE;
  const hasSelection = selectedProductIds.length > 0;

  const handleClear = () => {
    onClear();
    onClose();
  };

  return (
    <CSSTransition in={isOpen} timeout={TRANSITION_MS} classNames={styles} nodeRef={nodeRef} appear unmountOnExit>
      <div ref={nodeRef} className={styles.root}>
        <div className={styles.overlay} onClick={onClose} aria-hidden='true' />
        <dialog open className={styles.modalBox} aria-label={modalTitle}>
          {/* 着色フィルタの定義。filter: url(#id) は同一ドキュメント内の参照なのでモーダルと一緒に描画する */}
          <svg className={styles.tintFilterDefs} aria-hidden='true' focusable='false'>
            <defs>
              <filter id={SELECTED_TINT_FILTER_ID} colorInterpolationFilters='sRGB'>
                {/* flood-color は presentation attribute では var() を解決できないため style で渡す */}
                <feFlood style={{ floodColor: 'var(--color-red)' }} result='tint' />
                <feComposite in='tint' in2='SourceGraphic' operator='in' />
              </filter>
            </defs>
          </svg>
          <div className={styles.header}>
            <h2 className={styles.title}>{modalTitle}</h2>
            <button type='button' className={styles.closeButton} onClick={onClose} aria-label='閉じる'>
              <FaIcon icon={faXmark} />
            </button>
          </div>
          <ul className={styles.productList}>
            {products.map((product) => {
              const isSelected = selectedProductIds.includes(product.id);
              // 何かが選択されているとき、非選択アイテムは darkgray で控えめにする
              const isDimmed = hasSelection && !isSelected;
              // materialCount指定時のみ、対象の素材が0件のアイテムを選択できないよう非活性にする
              const isUnavailable = product.materialCount === 0;
              const hasMaterialCount = product.materialCount !== undefined;
              return (
                <li key={product.id}>
                  <button
                    type='button'
                    className={`${styles.productRow} ${resolveRowStateClass({ isUnavailable, isSelected, isDimmed })}`}
                    onClick={() => onToggleProduct(product.id)}
                    disabled={isUnavailable}
                  >
                    <div className={styles.productIconWrapper}>
                      <NextImageView
                        src={product.iconImageUrl}
                        alt={product.name}
                        fill={false}
                        width={32}
                        height={32}
                        objectFit='contain'
                        className={styles.productIcon}
                      />
                    </div>
                    <span className={styles.productName}>{product.name}</span>
                    {hasMaterialCount && <span className={styles.materialCount}>({product.materialCount})</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className={styles.footer}>
            <button type='button' className={styles.clearButton} onClick={handleClear}>
              クリア
            </button>
          </div>
        </dialog>
      </div>
    </CSSTransition>
  );
}

export default memo(ItemFilterModal);
